import { useState, useEffect, useRef } from "react";

const MOCK_DESTINATIONS = [
  {
    city: "San Diego",
    country: "USA",
    emoji: "🌊",
    transport: "bus",
    transportName: "FlixBus",
    transportPrice: 47,
    hotelPerNight: 89,
    travelTime: "4h 30m",
    tags: ["Beach", "Food", "Nightlife"],
    aiSummary: "San Diego is the laid-back cousin of LA — better food, fewer crowds, and arguably the best weather in the country. The Gaslamp Quarter comes alive at night, and you're never more than 15 minutes from a world-class beach. Don't skip the fish tacos in Ocean Beach.",
    weatherHigh: 68,
    weatherLow: 54,
  },
  {
    city: "Puerto Vallarta",
    country: "Mexico",
    emoji: "🌮",
    transport: "flight",
    transportName: "Volaris Direct",
    transportPrice: 189,
    hotelPerNight: 62,
    travelTime: "2h 45m",
    tags: ["Beach", "Culture", "Budget-Friendly"],
    aiSummary: "Puerto Vallarta gives you the Mexico experience without the Cancún tourist trap energy. The Malecón boardwalk at sunset is unreal, the street food in Zona Romántica is some of the best in the country, and your dollar stretches incredibly far.",
    weatherHigh: 82,
    weatherLow: 68,
  },
  {
    city: "Denver",
    country: "USA",
    emoji: "🏔️",
    transport: "flight",
    transportName: "Frontier Airlines",
    transportPrice: 78,
    hotelPerNight: 105,
    travelTime: "2h 10m",
    tags: ["Mountains", "Craft Beer", "Adventure"],
    aiSummary: "Denver is a gateway to the Rockies but a destination in its own right. The craft beer scene is world-class, RiNo district has incredible street art and restaurants, and you can be skiing within an hour. The mile-high air hits different.",
    weatherHigh: 48,
    weatherLow: 25,
  },
  {
    city: "New Orleans",
    country: "USA",
    emoji: "🎺",
    transport: "flight",
    transportName: "Spirit Airlines",
    transportPrice: 112,
    hotelPerNight: 95,
    travelTime: "3h 15m",
    tags: ["Food", "Music", "Culture"],
    aiSummary: "New Orleans doesn't need a special occasion — every night is an event. Skip Bourbon Street and head to Frenchmen Street for the real music scene. The food is a religion here: gumbo, po'boys, beignets at 2am. There's nowhere else like it.",
    weatherHigh: 72,
    weatherLow: 55,
  },
  {
    city: "Tucson",
    country: "USA",
    emoji: "🌵",
    transport: "bus",
    transportName: "Greyhound",
    transportPrice: 35,
    hotelPerNight: 72,
    travelTime: "7h 00m",
    tags: ["Desert", "Food", "Budget-Friendly"],
    aiSummary: "Tucson is a UNESCO City of Gastronomy — seriously. The Sonoran Mexican food here rivals anything across the border. Saguaro National Park surrounds the city with otherworldly desert landscapes. It's warm, cheap, and massively underappreciated.",
    weatherHigh: 78,
    weatherLow: 50,
  },
  {
    city: "Portland",
    country: "USA",
    emoji: "🌲",
    transport: "flight",
    transportName: "Alaska Airlines",
    transportPrice: 134,
    hotelPerNight: 110,
    travelTime: "2h 30m",
    tags: ["Food", "Nature", "Quirky"],
    aiSummary: "Portland is where independent culture thrives — from Powell's Books to the food cart pods to the craft coffee obsession. The gorge hikes are stunning, and you can day-trip to the coast or Mt. Hood. It's weird in the best way.",
    weatherHigh: 52,
    weatherLow: 38,
  },
];

const transportIcons = { bus: "🚌", train: "🚄", flight: "✈️" };

/* ─── Typing animation hook ─── */
function useTypewriter(text, speed = 35, startDelay = 300) {
  const [displayed, setDisplayed] = useState("");
  const [done, setDone] = useState(false);
  useEffect(() => {
    setDisplayed("");
    setDone(false);
    const timeout = setTimeout(() => {
      let i = 0;
      const interval = setInterval(() => {
        i++;
        setDisplayed(text.slice(0, i));
        if (i >= text.length) {
          clearInterval(interval);
          setDone(true);
        }
      }, speed);
      return () => clearInterval(interval);
    }, startDelay);
    return () => clearTimeout(timeout);
  }, [text]);
  return { displayed, done };
}

/* ─── Chat bubble components ─── */
function BotBubble({ text, delay = 0, onDone, children }) {
  const [show, setShow] = useState(delay === 0);
  useEffect(() => {
    if (delay > 0) {
      const t = setTimeout(() => setShow(true), delay);
      return () => clearTimeout(t);
    }
  }, [delay]);

  const { displayed, done } = useTypewriter(text, 30, show ? 200 : 99999);

  useEffect(() => {
    if (done && onDone) onDone();
  }, [done]);

  if (!show) return null;

  return (
    <div style={{
      display: "flex",
      gap: 12,
      alignItems: "flex-start",
      animation: "fadeUp 0.3s ease-out",
      marginBottom: 8,
    }}>
      <div style={{
        width: 36,
        height: 36,
        borderRadius: 12,
        background: "linear-gradient(135deg, #ff6b35, #e8552a)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: 14,
        fontWeight: 700,
        color: "#fff",
        flexShrink: 0,
        fontFamily: "'Space Mono', monospace",
      }}>
        G
      </div>
      <div style={{ maxWidth: "80%" }}>
        <div style={{
          background: "rgba(255,255,255,0.06)",
          border: "1px solid rgba(255,255,255,0.08)",
          borderRadius: "4px 18px 18px 18px",
          padding: "14px 18px",
          fontFamily: "'DM Sans', sans-serif",
          fontSize: 15,
          color: "#e0e0e0",
          lineHeight: 1.6,
        }}>
          {displayed}
          {!done && <span style={{ opacity: 0.5, animation: "blink 0.8s infinite" }}>▌</span>}
        </div>
        {done && children && (
          <div style={{ marginTop: 12, animation: "fadeUp 0.4s ease-out" }}>
            {children}
          </div>
        )}
      </div>
    </div>
  );
}

function UserBubble({ text }) {
  return (
    <div style={{
      display: "flex",
      justifyContent: "flex-end",
      marginBottom: 8,
      animation: "fadeUp 0.3s ease-out",
    }}>
      <div style={{
        background: "linear-gradient(135deg, #ff6b35, #e8552a)",
        borderRadius: "18px 4px 18px 18px",
        padding: "14px 18px",
        fontFamily: "'DM Sans', sans-serif",
        fontSize: 15,
        color: "#fff",
        maxWidth: "75%",
        lineHeight: 1.5,
      }}>
        {text}
      </div>
    </div>
  );
}

/* ─── Budget quick picks ─── */
function BudgetPicker({ onPick }) {
  const [custom, setCustom] = useState(false);
  const [customVal, setCustomVal] = useState("");
  const customRef = useRef(null);

  useEffect(() => {
    if (custom) customRef.current?.focus();
  }, [custom]);

  const options = [
    { label: "$500", sub: "budget escape", value: 500 },
    { label: "$800", sub: "solid trip", value: 800 },
    { label: "$1,200", sub: "treat yourself", value: 1200 },
    { label: "$2,000+", sub: "go big", value: 2000 },
  ];

  const handleCustomSubmit = () => {
    const num = parseInt(customVal.replace(/[^0-9]/g, ""), 10);
    if (num && num > 0) {
      onPick(num, `$${num.toLocaleString()}`);
    }
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
        {options.map(o => (
          <button key={o.value} onClick={() => { setCustom(false); onPick(o.value, o.label); }} style={{
            ...chipStyle,
            opacity: custom ? 0.5 : 1,
          }}>
            <span style={{ fontFamily: "'Space Mono', monospace", fontWeight: 600, fontSize: 15 }}>{o.label}</span>
            <span style={{ fontSize: 11, color: "#999", marginTop: 2 }}>{o.sub}</span>
          </button>
        ))}
        <button onClick={() => setCustom(c => !c)} style={{
          ...chipStyle,
          borderColor: custom ? "#ff6b35" : "rgba(255,255,255,0.1)",
          background: custom ? "rgba(255,107,53,0.12)" : "rgba(255,255,255,0.03)",
        }}>
          <span style={{ fontFamily: "'Space Mono', monospace", fontWeight: 600, fontSize: 15 }}>✎</span>
          <span style={{ fontSize: 11, color: custom ? "#ff6b35" : "#999", marginTop: 2 }}>Custom</span>
        </button>
      </div>
      {custom && (
        <div style={{ display: "flex", gap: 8, animation: "fadeUp 0.3s ease-out" }}>
          <div style={{ position: "relative", flex: 1 }}>
            <span style={{
              position: "absolute",
              left: 14,
              top: "50%",
              transform: "translateY(-50%)",
              color: "#ff6b35",
              fontFamily: "'Space Mono', monospace",
              fontSize: 16,
              fontWeight: 600,
            }}>$</span>
            <input
              ref={customRef}
              type="text"
              inputMode="numeric"
              value={customVal}
              onChange={e => setCustomVal(e.target.value.replace(/[^0-9]/g, ""))}
              onKeyDown={e => e.key === "Enter" && handleCustomSubmit()}
              placeholder="amount"
              style={{
                width: "100%",
                padding: "12px 14px 12px 32px",
                borderRadius: 12,
                border: "1px solid rgba(255,107,53,0.3)",
                background: "rgba(255,255,255,0.04)",
                color: "#fafafa",
                fontSize: 16,
                fontFamily: "'Space Mono', monospace",
                outline: "none",
              }}
            />
          </div>
          <button
            onClick={handleCustomSubmit}
            disabled={!customVal}
            style={{
              padding: "12px 20px",
              borderRadius: 12,
              border: "none",
              background: customVal ? "linear-gradient(135deg, #ff6b35, #e8552a)" : "rgba(255,255,255,0.06)",
              color: customVal ? "#fff" : "#555",
              fontFamily: "'DM Sans', sans-serif",
              fontSize: 14,
              fontWeight: 600,
              cursor: customVal ? "pointer" : "default",
              transition: "all 0.2s",
            }}
          >
            Go
          </button>
        </div>
      )}
    </div>
  );
}

/* ─── Transport picker ─── */
function TransportPicker({ onPick }) {
  const [selected, setSelected] = useState({ bus: false, train: false, flight: false });
  const anySelected = Object.values(selected).some(Boolean);
  const allSelected = selected.bus && selected.train && selected.flight;

  return (
    <div>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        {[
          { key: "bus", icon: "🚌", label: "Bus" },
          { key: "train", icon: "🚄", label: "Train" },
          { key: "flight", icon: "✈️", label: "Flight" },
        ].map(t => (
          <button
            key={t.key}
            onClick={() => setSelected(s => ({ ...s, [t.key]: !s[t.key] }))}
            style={{
              ...chipStyle,
              borderColor: selected[t.key] ? "#ff6b35" : "rgba(255,255,255,0.1)",
              background: selected[t.key] ? "rgba(255,107,53,0.12)" : "rgba(255,255,255,0.03)",
            }}
          >
            <span style={{ fontSize: 20 }}>{t.icon}</span>
            <span style={{ fontSize: 13 }}>{t.label}</span>
          </button>
        ))}
        <button
          onClick={() => {
            onPick({ bus: true, train: true, flight: true });
          }}
          style={{
            ...chipStyle,
            borderColor: "rgba(255,255,255,0.1)",
            background: "rgba(255,255,255,0.03)",
          }}
        >
          <span style={{ fontSize: 20 }}>🤷</span>
          <span style={{ fontSize: 13 }}>Don't care</span>
        </button>
      </div>
      {anySelected && !allSelected && (
        <button
          onClick={() => onPick(selected)}
          style={{
            marginTop: 12,
            padding: "10px 24px",
            borderRadius: 12,
            border: "none",
            background: "linear-gradient(135deg, #ff6b35, #e8552a)",
            color: "#fff",
            fontFamily: "'DM Sans', sans-serif",
            fontSize: 14,
            fontWeight: 600,
            cursor: "pointer",
            animation: "fadeUp 0.3s ease-out",
          }}
        >
          Let's go →
        </button>
      )}
    </div>
  );
}

const chipStyle = {
  padding: "12px 18px",
  borderRadius: 14,
  border: "1px solid rgba(255,255,255,0.1)",
  background: "rgba(255,255,255,0.03)",
  color: "#e0e0e0",
  cursor: "pointer",
  transition: "all 0.2s",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: 4,
  fontFamily: "'DM Sans', sans-serif",
  minWidth: 72,
};

/* ─── Date picker inline ─── */
function DatePicker({ onPick }) {
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const ready = from && to;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
        <div style={{ flex: 1, minWidth: 140 }}>
          <label style={{ fontSize: 10, color: "#666", fontFamily: "'Space Mono', monospace", letterSpacing: 1.5, display: "block", marginBottom: 6 }}>DEPART</label>
          <input type="date" value={from} onChange={e => setFrom(e.target.value)} style={dateInputStyle} />
        </div>
        <div style={{ flex: 1, minWidth: 140 }}>
          <label style={{ fontSize: 10, color: "#666", fontFamily: "'Space Mono', monospace", letterSpacing: 1.5, display: "block", marginBottom: 6 }}>RETURN</label>
          <input type="date" value={to} onChange={e => setTo(e.target.value)} style={dateInputStyle} />
        </div>
      </div>
      {ready && (
        <button
          onClick={() => {
            const nights = Math.max(1, Math.round((new Date(to) - new Date(from)) / 86400000));
            const fromStr = new Date(from).toLocaleDateString("en-US", { month: "short", day: "numeric" });
            const toStr = new Date(to).toLocaleDateString("en-US", { month: "short", day: "numeric" });
            onPick(from, to, nights, `${fromStr} – ${toStr}`);
          }}
          style={{
            padding: "10px 24px",
            borderRadius: 12,
            border: "none",
            background: "linear-gradient(135deg, #ff6b35, #e8552a)",
            color: "#fff",
            fontFamily: "'DM Sans', sans-serif",
            fontSize: 14,
            fontWeight: 600,
            cursor: "pointer",
            alignSelf: "flex-start",
            animation: "fadeUp 0.3s ease-out",
          }}
        >
          Lock it in
        </button>
      )}
    </div>
  );
}

const dateInputStyle = {
  width: "100%",
  padding: "12px 14px",
  borderRadius: 12,
  border: "1px solid rgba(255,255,255,0.1)",
  background: "rgba(255,255,255,0.04)",
  color: "#fafafa",
  fontSize: 14,
  fontFamily: "'Space Mono', monospace",
  outline: "none",
};

/* ─── Text input for city ─── */
function CityInput({ onSubmit }) {
  const [val, setVal] = useState("");
  const ref = useRef(null);
  useEffect(() => { ref.current?.focus(); }, []);

  return (
    <div style={{ display: "flex", gap: 8 }}>
      <input
        ref={ref}
        value={val}
        onChange={e => setVal(e.target.value)}
        onKeyDown={e => e.key === "Enter" && val.trim() && onSubmit(val.trim())}
        placeholder="Type your city..."
        style={{
          flex: 1,
          padding: "12px 16px",
          borderRadius: 14,
          border: "1px solid rgba(255,255,255,0.1)",
          background: "rgba(255,255,255,0.04)",
          color: "#fafafa",
          fontSize: 15,
          fontFamily: "'DM Sans', sans-serif",
          outline: "none",
        }}
      />
      <button
        onClick={() => val.trim() && onSubmit(val.trim())}
        style={{
          padding: "12px 20px",
          borderRadius: 14,
          border: "none",
          background: val.trim() ? "linear-gradient(135deg, #ff6b35, #e8552a)" : "rgba(255,255,255,0.05)",
          color: val.trim() ? "#fff" : "#555",
          fontFamily: "'DM Sans', sans-serif",
          fontSize: 14,
          fontWeight: 600,
          cursor: val.trim() ? "pointer" : "default",
          transition: "all 0.3s",
        }}
      >
        →
      </button>
    </div>
  );
}

/* ─── Conversation Flow ─── */
function ConversationInput({ onComplete }) {
  const [step, setStep] = useState(-1);
  const [messages, setMessages] = useState([]);
  const [data, setData] = useState({ origin: "", budget: 0, dateFrom: "", dateTo: "", nights: 0, transport: {} });
  const [botReady, setBotReady] = useState(false);
  const [inputVal, setInputVal] = useState("I need to GTFOH...");
  const bottomRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, step, botReady]);

  const addUserMsg = (text) => {
    setMessages(m => [...m, { type: "user", text }]);
  };

  const handleTextSend = () => {
    const val = inputVal.trim();
    if (!val) return;

    if (step === -1) {
      // Initial send
      addUserMsg(val);
      setInputVal("");
      setTimeout(() => setStep(0), 400);
    } else if (step === 0 && botReady) {
      // City answer
      addUserMsg(val);
      setData(d => ({ ...d, origin: val }));
      setInputVal("");
      setBotReady(false);
      setTimeout(() => setStep(1), 300);
    }
  };

  // Determine what the bottom bar shows
  const showTextInput = step === -1 || step === 0;
  const showBudgetPicker = step === 1 && botReady;
  const showDatePicker = step === 2 && botReady;
  const showTransportPicker = step === 3 && botReady;
  const showNothing = step === 4 || (step >= 0 && !botReady && !showTextInput);

  const getPlaceholder = () => {
    if (step === -1) return "I need to GTFOH...";
    if (step === 0) return "Type your city...";
    return "";
  };

  return (
    <div style={{
      minHeight: "100vh",
      background: "#0a0a0a",
      display: "flex",
      flexDirection: "column",
      position: "relative",
      overflow: "hidden",
    }}>
      {/* Ambient glow */}
      <div style={{
        position: "fixed",
        top: "-30%",
        left: "-20%",
        width: "70%",
        height: "70%",
        background: "radial-gradient(ellipse, rgba(255, 107, 53, 0.06) 0%, transparent 70%)",
        pointerEvents: "none",
      }} />
      <div style={{
        position: "fixed",
        bottom: "-30%",
        right: "-20%",
        width: "60%",
        height: "60%",
        background: "radial-gradient(ellipse, rgba(56, 189, 248, 0.04) 0%, transparent 70%)",
        pointerEvents: "none",
      }} />

      {/* Header */}
      <div style={{
        padding: "24px 20px 16px",
        textAlign: "center",
        borderBottom: "1px solid rgba(255,255,255,0.05)",
        position: "relative",
        zIndex: 2,
      }}>
        <h1 style={{
          fontFamily: "'Space Mono', monospace",
          fontSize: 28,
          fontWeight: 700,
          color: "#fafafa",
          margin: 0,
          letterSpacing: "-0.02em",
        }}>
          <span style={{
            background: "linear-gradient(135deg, #ff6b35, #f7c948)",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
          }}>GTFOH</span>
        </h1>
      </div>

      {/* Chat area */}
      <div style={{
        flex: 1,
        overflowY: "auto",
        padding: "24px 20px",
        maxWidth: 600,
        width: "100%",
        margin: "0 auto",
        position: "relative",
        zIndex: 2,
        display: "flex",
        flexDirection: "column",
        justifyContent: step === -1 ? "flex-end" : "flex-start",
      }}>
        {/* Rendered past messages */}
        {messages.map((m, i) => (
          m.type === "user"
            ? <UserBubble key={i} text={m.text} />
            : <div key={i} style={{ display: "flex", gap: 12, alignItems: "flex-start", marginBottom: 8 }}>
                <div style={{
                  width: 36, height: 36, borderRadius: 12,
                  background: "linear-gradient(135deg, #ff6b35, #e8552a)",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  fontSize: 14, fontWeight: 700, color: "#fff", flexShrink: 0,
                  fontFamily: "'Space Mono', monospace",
                }}>G</div>
                <div style={{
                  background: "rgba(255,255,255,0.06)",
                  border: "1px solid rgba(255,255,255,0.08)",
                  borderRadius: "4px 18px 18px 18px",
                  padding: "14px 18px",
                  fontFamily: "'DM Sans', sans-serif",
                  fontSize: 15, color: "#e0e0e0", lineHeight: 1.6,
                  maxWidth: "80%",
                }}>{m.text}</div>
              </div>
        ))}

        {/* Step 0: Where are you escaping from? */}
        {step === 0 && (
          <BotBubble
            text="I got you. Where are you escaping from?"
            onDone={() => { setBotReady(true); setTimeout(() => inputRef.current?.focus(), 100); }}
          />
        )}

        {/* Step 1: Budget */}
        {step === 1 && (
          <BotBubble
            text={`${data.origin} — word. How much you got? Total?`}
            onDone={() => setBotReady(true)}
          />
        )}

        {/* Step 2: Dates */}
        {step === 2 && (
          <BotBubble
            text="When do you need to GTFOH? Pick your dates."
            onDone={() => setBotReady(true)}
          />
        )}

        {/* Step 3: Transport */}
        {step === 3 && (
          <BotBubble
            text={`${data.nights} night${data.nights !== 1 ? 's' : ''} — nice. Last thing. How do you want to travel?`}
            onDone={() => setBotReady(true)}
          />
        )}

        {/* Step 4: Searching */}
        {step === 4 && (
          <BotBubble
            text={`Got it. Let me find everywhere you can go from ${data.origin} for $${data.budget}. Give me a sec...`}
            onDone={() => {
              setTimeout(() => onComplete(data), 2000);
            }}
          />
        )}

        <div ref={bottomRef} />
      </div>

      {/* Bottom input bar — always visible, content changes per step */}
      <div style={{
        padding: "12px 20px 24px",
        borderTop: "1px solid rgba(255,255,255,0.05)",
        position: "relative",
        zIndex: 2,
        background: "rgba(10,10,10,0.95)",
        backdropFilter: "blur(10px)",
      }}>
        <div style={{
          maxWidth: 600,
          margin: "0 auto",
        }}>
          {/* Text input mode (initial send + city) */}
          {showTextInput && (
            <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
              <input
                ref={inputRef}
                value={inputVal}
                onChange={e => setInputVal(e.target.value)}
                onKeyDown={e => e.key === "Enter" && handleTextSend()}
                placeholder={getPlaceholder()}
                style={{
                  flex: 1,
                  padding: "14px 18px",
                  borderRadius: 16,
                  border: "1px solid rgba(255,255,255,0.1)",
                  background: "rgba(255,255,255,0.04)",
                  color: "#fafafa",
                  fontSize: 15,
                  fontFamily: "'DM Sans', sans-serif",
                  outline: "none",
                  transition: "all 0.3s",
                }}
              />
              <button
                onClick={handleTextSend}
                style={{
                  width: 48,
                  height: 48,
                  borderRadius: 14,
                  border: "none",
                  background: inputVal.trim() ? "linear-gradient(135deg, #ff6b35, #e8552a)" : "rgba(255,255,255,0.06)",
                  color: inputVal.trim() ? "#fff" : "#555",
                  fontSize: 20,
                  cursor: inputVal.trim() ? "pointer" : "default",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                  transition: "all 0.2s",
                  boxShadow: inputVal.trim() ? "0 4px 20px rgba(255, 107, 53, 0.3)" : "none",
                  animation: step === -1 && inputVal.trim() ? "pulse 2s ease-in-out infinite" : "none",
                }}
              >
                ↑
              </button>
            </div>
          )}

          {/* Budget picker mode */}
          {showBudgetPicker && (
            <div style={{ animation: "fadeUp 0.3s ease-out" }}>
              <BudgetPicker onPick={(val, label) => {
                addUserMsg(label);
                setData(d => ({ ...d, budget: val }));
                setBotReady(false);
                setTimeout(() => setStep(2), 300);
              }} />
            </div>
          )}

          {/* Date picker mode */}
          {showDatePicker && (
            <div style={{ animation: "fadeUp 0.3s ease-out" }}>
              <DatePicker onPick={(from, to, nights, label) => {
                addUserMsg(label);
                setData(d => ({ ...d, dateFrom: from, dateTo: to, nights }));
                setBotReady(false);
                setTimeout(() => setStep(3), 300);
              }} />
            </div>
          )}

          {/* Transport picker mode */}
          {showTransportPicker && (
            <div style={{ animation: "fadeUp 0.3s ease-out" }}>
              <TransportPicker onPick={(transport) => {
                const modes = Object.entries(transport).filter(([, v]) => v).map(([k]) => k);
                const label = modes.length === 3 ? "Whatever gets me there" : modes.map(m => m.charAt(0).toUpperCase() + m.slice(1)).join(" + ");
                addUserMsg(label);
                setData(d => ({ ...d, transport }));
                setBotReady(false);
                setTimeout(() => setStep(4), 300);
              }} />
            </div>
          )}

          {/* Waiting / searching state */}
          {showNothing && step !== -1 && (
            <div style={{
              textAlign: "center",
              padding: "10px 0",
              fontFamily: "'Space Mono', monospace",
              fontSize: 12,
              color: "#333",
              letterSpacing: 1,
            }}>
              {step === 4 ? "searching..." : ""}
            </div>
          )}
        </div>
      </div>

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;600;700&family=DM+Sans:wght@400;500;600&family=Space+Mono:wght@400;700&display=swap');
        @keyframes fadeUp { from { opacity: 0; transform: translateY(12px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes blink { 0%, 50% { opacity: 1; } 51%, 100% { opacity: 0; } }
        @keyframes pulse { 0%, 100% { box-shadow: 0 4px 20px rgba(255, 107, 53, 0.3); } 50% { box-shadow: 0 4px 30px rgba(255, 107, 53, 0.6); } }
        * { box-sizing: border-box; }
        input[type="date"]::-webkit-calendar-picker-indicator { filter: invert(0.5); cursor: pointer; }
        ::-webkit-scrollbar { width: 6px; }
        ::-webkit-scrollbar-track { background: transparent; }
        ::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.1); border-radius: 3px; }
      `}</style>
    </div>
  );
}

/* ─── Loading Screen ─── */
function LoadingScreen() {
  const [step, setStep] = useState(0);
  const steps = [
    "Scanning transport routes...",
    "Checking hotel availability...",
    "Calculating all-in costs...",
    "AI analyzing destinations...",
    "Ranking your best options...",
  ];
  useEffect(() => {
    const i = setInterval(() => setStep(s => Math.min(s + 1, steps.length - 1)), 600);
    return () => clearInterval(i);
  }, []);

  return (
    <div style={{
      minHeight: "100vh",
      background: "#0a0a0a",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      gap: 36,
    }}>
      <div style={{
        fontFamily: "'Space Mono', monospace",
        fontSize: 24,
        fontWeight: 700,
        background: "linear-gradient(135deg, #ff6b35, #f7c948)",
        WebkitBackgroundClip: "text",
        WebkitTextFillColor: "transparent",
      }}>GTFOH</div>
      <div style={{
        width: 44, height: 44,
        border: "3px solid rgba(255,107,53,0.15)",
        borderTopColor: "#ff6b35",
        borderRadius: "50%",
        animation: "spin 0.8s linear infinite",
      }} />
      <div style={{ textAlign: "center" }}>
        {steps.map((s, i) => (
          <div key={i} style={{
            fontFamily: "'Space Mono', monospace",
            fontSize: 12,
            color: i < step ? "#444" : i === step ? "#ff6b35" : "transparent",
            marginBottom: 6,
            transition: "all 0.4s",
            opacity: i <= step ? 1 : 0,
          }}>
            {i < step ? "✓ " : "› "}{s}
          </div>
        ))}
      </div>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Space+Mono:wght@400;700&display=swap');
        @keyframes spin { to { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
}

/* ─── Animated counter ─── */
function AnimatedCounter({ target, prefix = "$" }) {
  const [count, setCount] = useState(0);
  const started = useRef(false);
  useEffect(() => {
    if (started.current) return;
    started.current = true;
    const start = performance.now();
    const animate = (now) => {
      const p = Math.min((now - start) / 1200, 1);
      setCount(Math.round((1 - Math.pow(1 - p, 3)) * target));
      if (p < 1) requestAnimationFrame(animate);
    };
    requestAnimationFrame(animate);
  }, [target]);
  return <span>{prefix}{count}</span>;
}

/* ─── Destination Card ─── */
function DestinationCard({ dest, nights, index, expanded, onToggle }) {
  const totalHotel = dest.hotelPerNight * nights;
  const totalCost = dest.transportPrice + totalHotel;

  return (
    <div
      style={{
        background: "rgba(255,255,255,0.03)",
        border: "1px solid rgba(255,255,255,0.08)",
        borderRadius: 20,
        overflow: "hidden",
        transition: "all 0.3s",
        animation: `fadeUp 0.5s ease-out ${index * 0.08}s both`,
        cursor: "pointer",
      }}
      onMouseEnter={e => {
        e.currentTarget.style.borderColor = "rgba(255,107,53,0.3)";
        e.currentTarget.style.transform = "translateY(-3px)";
      }}
      onMouseLeave={e => {
        e.currentTarget.style.borderColor = "rgba(255,255,255,0.08)";
        e.currentTarget.style.transform = "translateY(0)";
      }}
    >
      <div onClick={onToggle} style={{ padding: "22px 22px 18px" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 14 }}>
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 4 }}>
              <span style={{ fontSize: 26 }}>{dest.emoji}</span>
              <h3 style={{
                fontFamily: "'Space Mono', monospace",
                fontSize: 22,
                fontWeight: 700,
                color: "#fafafa",
                margin: 0,
              }}>{dest.city}</h3>
            </div>
            <span style={{ fontFamily: "'DM Sans', sans-serif", fontSize: 13, color: "#666" }}>{dest.country}</span>
          </div>
          <div style={{ textAlign: "right" }}>
            <div style={{
              fontFamily: "'Space Mono', monospace",
              fontSize: 26,
              fontWeight: 700,
              color: "#ff6b35",
              lineHeight: 1,
            }}>
              <AnimatedCounter target={totalCost} />
            </div>
            <div style={{
              fontFamily: "'Space Mono', monospace",
              fontSize: 10,
              color: "#555",
              letterSpacing: 1.5,
              marginTop: 4,
            }}>ALL-IN</div>
          </div>
        </div>

        {/* Cost bar */}
        <div style={{ marginBottom: 14 }}>
          <div style={{ display: "flex", borderRadius: 4, overflow: "hidden", height: 5 }}>
            <div style={{ width: `${(dest.transportPrice / totalCost) * 100}%`, background: "#ff6b35" }} />
            <div style={{ width: `${(totalHotel / totalCost) * 100}%`, background: "#38bdf8" }} />
          </div>
          <div style={{ display: "flex", justifyContent: "space-between", marginTop: 8 }}>
            <span style={{ fontFamily: "'Space Mono', monospace", fontSize: 11, color: "#ff6b35" }}>
              {transportIcons[dest.transport]} ${dest.transportPrice} {dest.transportName}
            </span>
            <span style={{ fontFamily: "'Space Mono', monospace", fontSize: 11, color: "#38bdf8" }}>
              🏨 ${totalHotel} ({nights}n × ${dest.hotelPerNight})
            </span>
          </div>
        </div>

        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div style={{ display: "flex", gap: 6 }}>
            {dest.tags.map(tag => (
              <span key={tag} style={{
                padding: "3px 10px",
                borderRadius: 20,
                background: "rgba(255,255,255,0.04)",
                border: "1px solid rgba(255,255,255,0.07)",
                fontFamily: "'DM Sans', sans-serif",
                fontSize: 11,
                color: "#999",
              }}>{tag}</span>
            ))}
          </div>
          <span style={{ fontFamily: "'Space Mono', monospace", fontSize: 11, color: "#555" }}>
            ⏱ {dest.travelTime}
          </span>
        </div>
      </div>

      {expanded && (
        <div style={{ padding: "0 22px 22px", animation: "fadeUp 0.3s ease-out" }}>
          <div style={{
            background: "rgba(255,107,53,0.04)",
            border: "1px solid rgba(255,107,53,0.1)",
            borderRadius: 14,
            padding: 18,
          }}>
            <div style={{
              fontSize: 10,
              fontFamily: "'Space Mono', monospace",
              color: "#ff6b35",
              letterSpacing: 2,
              marginBottom: 10,
            }}>✦ AI INSIGHT</div>
            <p style={{
              fontFamily: "'DM Sans', sans-serif",
              fontSize: 14,
              color: "#ccc",
              lineHeight: 1.7,
              margin: 0,
            }}>{dest.aiSummary}</p>
            <div style={{ display: "flex", gap: 16, marginTop: 14, paddingTop: 14, borderTop: "1px solid rgba(255,255,255,0.05)" }}>
              <div>
                <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 10, color: "#555" }}>HIGH</div>
                <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 15, color: "#f7c948" }}>{dest.weatherHigh}°F</div>
              </div>
              <div>
                <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 10, color: "#555" }}>LOW</div>
                <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 15, color: "#38bdf8" }}>{dest.weatherLow}°F</div>
              </div>
            </div>
          </div>
          <button style={{
            width: "100%",
            marginTop: 12,
            padding: "13px 0",
            borderRadius: 12,
            border: "1px solid rgba(255,107,53,0.25)",
            background: "rgba(255,107,53,0.06)",
            color: "#ff6b35",
            fontFamily: "'DM Sans', sans-serif",
            fontSize: 14,
            fontWeight: 600,
            cursor: "pointer",
          }}>
            Generate full itinerary →
          </button>
        </div>
      )}
    </div>
  );
}

/* ─── Results Page ─── */
function ResultsPage({ searchParams, results: liveResults, onBack, onUpdateBudget }) {
  const [expandedIndex, setExpandedIndex] = useState(null);
  const [sortBy, setSortBy] = useState("price");
  const [filterTransport, setFilterTransport] = useState("all");
  const [editingBudget, setEditingBudget] = useState(false);
  const [newBudget, setNewBudget] = useState("");
  const budgetInputRef = useRef(null);
  const { nights, budget, origin } = searchParams;

  useEffect(() => {
    if (editingBudget) budgetInputRef.current?.focus();
  }, [editingBudget]);

  // Use live results if available, otherwise filter mock data
  let filtered = liveResults && liveResults.length > 0
    ? [...liveResults]
    : MOCK_DESTINATIONS.filter(d => {
        const total = d.transportPrice + d.hotelPerNight * nights;
        if (total > budget) return false;
        return true;
      });

  // Apply transport filter
  if (filterTransport !== "all") {
    filtered = filtered.filter(d => d.transport === filterTransport);
  }

  if (sortBy === "price") filtered.sort((a, b) => (a.transportPrice + a.hotelPerNight * nights) - (b.transportPrice + b.hotelPerNight * nights));
  else filtered.sort((a, b) => parseFloat(a.travelTime) - parseFloat(b.travelTime));

  const cheapest = filtered.length > 0 ? filtered[0].transportPrice + filtered[0].hotelPerNight * nights : 0;
  const avg = filtered.length > 0 ? Math.round(filtered.reduce((s, d) => s + d.transportPrice + d.hotelPerNight * nights, 0) / filtered.length) : 0;
  const noResults = filtered.length === 0;

  const handleBudgetSubmit = () => {
    const num = parseInt(newBudget.replace(/[^0-9]/g, ""), 10);
    if (num && num > 0) {
      setEditingBudget(false);
      setNewBudget("");
      onUpdateBudget(num);
    }
  };

  return (
    <div style={{
      minHeight: "100vh",
      background: "#0a0a0a",
      padding: "0 20px 60px",
    }}>
      <div style={{ maxWidth: 620, margin: "0 auto", paddingTop: 28 }}>
        <button onClick={onBack} style={{
          background: "none", border: "none", color: "#555",
          fontFamily: "'DM Sans', sans-serif", fontSize: 14, cursor: "pointer", padding: 0, marginBottom: 20,
        }}>← Start over</button>

        <div style={{ animation: "fadeUp 0.5s ease-out" }}>
          <div style={{
            fontFamily: "'Space Mono', monospace",
            fontSize: 11,
            color: noResults ? "#e8552a" : "#ff6b35",
            letterSpacing: 3,
            marginBottom: 8,
          }}>{noResults ? "0 DESTINATIONS FOUND" : `${filtered.length} DESTINATIONS FOUND`}</div>
          <h2 style={{
            fontFamily: "'Space Mono', monospace",
            fontSize: "clamp(24px, 5vw, 34px)",
            fontWeight: 700,
            color: "#fafafa",
            margin: "0 0 6px",
            lineHeight: 1.2,
          }}>
            {noResults ? (
              <>Nothing for{" "}
                <span style={{
                  background: "linear-gradient(135deg, #e8552a, #ff6b35)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                }}>${budget}</span>
              </>
            ) : (
              <>Here's where{" "}
                <span style={{
                  background: "linear-gradient(135deg, #ff6b35, #f7c948)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                }}>${budget}</span>{" "}takes you
              </>
            )}
          </h2>
          <p style={{
            fontFamily: "'DM Sans', sans-serif",
            fontSize: 14,
            color: "#666",
            margin: "0 0 24px",
          }}>
            From {origin} · {nights} night{nights !== 1 ? "s" : ""} · Transport + hotel
          </p>
        </div>

        {/* Stats row — budget tile is clickable when no results */}
        {!noResults && (
          <div style={{ display: "flex", gap: 10, marginBottom: 24, animation: "fadeUp 0.5s ease-out 0.1s both" }}>
            {[
              { label: "CHEAPEST", value: `$${cheapest}`, color: "#22c55e" },
              { label: "AVERAGE", value: `$${avg}`, color: "#f7c948" },
              { label: "BUDGET LEFT", value: `$${budget - cheapest}`, color: "#38bdf8" },
            ].map(s => (
              <div key={s.label} style={{
                flex: 1,
                background: "rgba(255,255,255,0.03)",
                border: "1px solid rgba(255,255,255,0.06)",
                borderRadius: 14,
                padding: "12px 14px",
                textAlign: "center",
              }}>
                <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, color: "#555", letterSpacing: 1.5, marginBottom: 4 }}>{s.label}</div>
                <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 18, fontWeight: 600, color: s.color }}>{s.value}</div>
              </div>
            ))}
          </div>
        )}

        {/* Empty state with clickable budget tile */}
        {noResults && (
          <div style={{
            textAlign: "center",
            padding: "40px 20px",
            animation: "fadeUp 0.5s ease-out 0.1s both",
          }}>
            <div style={{ fontSize: 48, marginBottom: 20 }}>💸</div>
            <p style={{
              fontFamily: "'DM Sans', sans-serif",
              fontSize: 16,
              color: "#888",
              marginBottom: 28,
            }}>
              Your budget's too tight for these dates. Bump it up and we'll search again.
            </p>

            {!editingBudget ? (
              <button
                onClick={() => setEditingBudget(true)}
                style={{
                  background: "rgba(255,107,53,0.08)",
                  border: "2px dashed rgba(255,107,53,0.4)",
                  borderRadius: 16,
                  padding: "20px 32px",
                  cursor: "pointer",
                  transition: "all 0.2s",
                  display: "inline-flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: 6,
                }}
                onMouseEnter={e => {
                  e.currentTarget.style.borderColor = "#ff6b35";
                  e.currentTarget.style.background = "rgba(255,107,53,0.12)";
                }}
                onMouseLeave={e => {
                  e.currentTarget.style.borderColor = "rgba(255,107,53,0.4)";
                  e.currentTarget.style.background = "rgba(255,107,53,0.08)";
                }}
              >
                <div style={{
                  fontFamily: "'Space Mono', monospace",
                  fontSize: 9,
                  color: "#ff6b35",
                  letterSpacing: 2,
                }}>YOUR BUDGET</div>
                <div style={{
                  fontFamily: "'Space Mono', monospace",
                  fontSize: 28,
                  fontWeight: 700,
                  color: "#ff6b35",
                }}>${budget}</div>
                <div style={{
                  fontFamily: "'DM Sans', sans-serif",
                  fontSize: 12,
                  color: "#ff6b35",
                  opacity: 0.7,
                  marginTop: 2,
                }}>tap to increase →</div>
              </button>
            ) : (
              <div style={{
                display: "inline-flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 12,
                animation: "fadeUp 0.3s ease-out",
              }}>
                <div style={{
                  fontFamily: "'Space Mono', monospace",
                  fontSize: 9,
                  color: "#ff6b35",
                  letterSpacing: 2,
                }}>NEW BUDGET</div>
                <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                  <div style={{ position: "relative" }}>
                    <span style={{
                      position: "absolute",
                      left: 14,
                      top: "50%",
                      transform: "translateY(-50%)",
                      color: "#ff6b35",
                      fontFamily: "'Space Mono', monospace",
                      fontSize: 22,
                      fontWeight: 700,
                    }}>$</span>
                    <input
                      ref={budgetInputRef}
                      type="text"
                      inputMode="numeric"
                      value={newBudget}
                      onChange={e => setNewBudget(e.target.value.replace(/[^0-9]/g, ""))}
                      onKeyDown={e => e.key === "Enter" && handleBudgetSubmit()}
                      placeholder={String(budget)}
                      style={{
                        width: 160,
                        padding: "14px 14px 14px 38px",
                        borderRadius: 14,
                        border: "2px solid #ff6b35",
                        background: "rgba(255,107,53,0.06)",
                        color: "#fafafa",
                        fontSize: 22,
                        fontFamily: "'Space Mono', monospace",
                        fontWeight: 700,
                        outline: "none",
                        textAlign: "center",
                      }}
                    />
                  </div>
                  <button
                    onClick={handleBudgetSubmit}
                    disabled={!newBudget}
                    style={{
                      padding: "14px 22px",
                      borderRadius: 14,
                      border: "none",
                      background: newBudget ? "linear-gradient(135deg, #ff6b35, #e8552a)" : "rgba(255,255,255,0.06)",
                      color: newBudget ? "#fff" : "#555",
                      fontFamily: "'DM Sans', sans-serif",
                      fontSize: 15,
                      fontWeight: 600,
                      cursor: newBudget ? "pointer" : "default",
                      transition: "all 0.2s",
                      boxShadow: newBudget ? "0 4px 20px rgba(255, 107, 53, 0.3)" : "none",
                    }}
                  >
                    Search
                  </button>
                </div>
                <button
                  onClick={() => setEditingBudget(false)}
                  style={{
                    background: "none",
                    border: "none",
                    color: "#555",
                    fontFamily: "'DM Sans', sans-serif",
                    fontSize: 12,
                    cursor: "pointer",
                  }}
                >
                  cancel
                </button>
              </div>
            )}
          </div>
        )}

        {/* Filters */}
        {!noResults && (
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
            <div style={{ display: "flex", gap: 6 }}>
              {["all", "bus", "train", "flight"].map(t => (
                <button key={t} onClick={() => setFilterTransport(t)} style={{
                  padding: "7px 13px", borderRadius: 10,
                  border: filterTransport === t ? "1px solid #ff6b35" : "1px solid rgba(255,255,255,0.07)",
                  background: filterTransport === t ? "rgba(255,107,53,0.1)" : "transparent",
                  color: filterTransport === t ? "#ff6b35" : "#555",
                  fontFamily: "'DM Sans', sans-serif", fontSize: 12, cursor: "pointer",
                }}>
                  {t === "all" ? "All" : `${transportIcons[t]} ${t.charAt(0).toUpperCase() + t.slice(1)}`}
                </button>
              ))}
            </div>
            <select value={sortBy} onChange={e => setSortBy(e.target.value)} style={{
              padding: "7px 12px", borderRadius: 10,
              border: "1px solid rgba(255,255,255,0.07)",
              background: "rgba(255,255,255,0.03)", color: "#888",
              fontFamily: "'DM Sans', sans-serif", fontSize: 12, cursor: "pointer", outline: "none",
            }}>
              <option value="price">Cheapest</option>
              <option value="time">Fastest</option>
            </select>
          </div>
        )}

        {/* Cards */}
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          {filtered.map((dest, i) => (
            <DestinationCard key={dest.city} dest={dest} nights={nights} index={i}
              expanded={expandedIndex === i} onToggle={() => setExpandedIndex(expandedIndex === i ? null : i)} />
          ))}
        </div>

        <div style={{ textAlign: "center", marginTop: 44, paddingTop: 20, borderTop: "1px solid rgba(255,255,255,0.05)" }}>
          <p style={{ fontFamily: "'Space Mono', monospace", fontSize: 10, color: "#333", letterSpacing: 1 }}>
            PROTOTYPE · LIVE PRICING COMING SOON
          </p>
        </div>
      </div>

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;600;700&family=DM+Sans:wght@400;500;600&family=Space+Mono:wght@400;700&display=swap');
        @keyframes fadeUp { from { opacity: 0; transform: translateY(15px); } to { opacity: 1; transform: translateY(0); } }
        * { box-sizing: border-box; }
        select option { background: #1a1a1a; color: #ccc; }
      `}</style>
    </div>
  );
}

/* ─── App Shell ─── */

// Set this to your Firebase Search Function URL after deploying
const API_BASE = process.env.REACT_APP_API_URL || "";
const LOOKUP_URL = API_BASE ? API_BASE.replace("search-", "lookupcity-") : "";

// Common city name to IATA code mapping (fallback)
const CITY_CODES = {
  "las vegas": "LAS", "los angeles": "LAX", "new york": "JFK", "nyc": "JFK",
  "chicago": "ORD", "denver": "DEN", "san francisco": "SFO", "seattle": "SEA",
  "miami": "MIA", "new orleans": "MSY", "san diego": "SAN", "austin": "AUS",
  "portland": "PDX", "phoenix": "PHX", "dallas": "DFW", "houston": "IAH",
  "atlanta": "ATL", "boston": "BOS", "nashville": "BNA", "orlando": "MCO",
  "salt lake city": "SLC", "minneapolis": "MSP", "detroit": "DTW",
  "philadelphia": "PHL", "charlotte": "CLT", "tampa": "TPA", "san antonio": "SAT",
  "honolulu": "HNL", "anchorage": "ANC", "dayton": "DAY", "reno": "RNO",
  "cancun": "CUN", "cancún": "CUN", "puerto vallarta": "PVR",
  "san juan": "SJU", "nassau": "NAS", "toronto": "YYZ", "vancouver": "YVR",
  "london": "LHR", "paris": "CDG", "rome": "FCO", "tokyo": "NRT",
};

async function resolveAirportCode(cityInput) {
  const input = cityInput.trim();

  // If already an IATA code (3 uppercase letters)
  if (/^[A-Z]{3}$/.test(input.toUpperCase()) && input.length === 3) {
    return input.toUpperCase();
  }

  // Check local lookup first
  const localMatch = CITY_CODES[input.toLowerCase()];
  if (localMatch) return localMatch;

  // Try Amadeus city lookup API
  if (LOOKUP_URL) {
    try {
      const url = new URL(LOOKUP_URL);
      url.searchParams.set("query", input);
      const resp = await fetch(url.toString());
      const data = await resp.json();
      if (data.results && data.results.length > 0) {
        // Prefer airport codes over city codes
        const airport = data.results.find(r => r.type === "AIRPORT");
        return airport ? airport.iataCode : data.results[0].iataCode;
      }
    } catch (e) {
      console.error("City lookup failed:", e);
    }
  }

  // Last resort: uppercase the input
  return input.toUpperCase().substring(0, 3);
}

async function fetchDestinations(params) {
  const { origin, budget, dateFrom, dateTo } = params;
  const originCode = await resolveAirportCode(origin);

  if (API_BASE) {
    try {
      const url = new URL(API_BASE);
      url.searchParams.set("origin", originCode);
      url.searchParams.set("departDate", dateFrom);
      url.searchParams.set("returnDate", dateTo);
      url.searchParams.set("budget", String(budget));
      
      const response = await fetch(url.toString());
      const data = await response.json();

      if (data.destinations && data.destinations.length > 0) {
        return data.destinations;
      }
    } catch (error) {
      console.error("API search failed, falling back to mock data:", error);
    }
  }

  // Fallback to mock data when API isn't configured yet
  const nights = Math.max(1, Math.round((new Date(dateTo) - new Date(dateFrom)) / 86400000));
  return MOCK_DESTINATIONS.filter(d => {
    const total = d.transportPrice + d.hotelPerNight * nights;
    return total <= budget;
  });
}

export default function App() {
  const [page, setPage] = useState("chat");
  const [searchParams, setSearchParams] = useState(null);
  const [results, setResults] = useState([]);

  const runSearch = async (data) => {
    setSearchParams(data);
    setPage("loading");
    try {
      const destinations = await fetchDestinations(data);
      setResults(destinations);
      // Small delay to ensure state updates before page transition
      await new Promise(r => setTimeout(r, 100));
    } catch (error) {
      console.error("Search error:", error);
      setResults([]);
    }
    setPage("results");
  };

  const handleComplete = (data) => {
    runSearch(data);
  };

  const handleUpdateBudget = (newBudget) => {
    const updated = { ...searchParams, budget: newBudget };
    runSearch(updated);
  };

  if (page === "chat") return <ConversationInput onComplete={handleComplete} />;
  if (page === "loading") return <LoadingScreen />;
  return <ResultsPage searchParams={searchParams} results={results} onBack={() => setPage("chat")} onUpdateBudget={handleUpdateBudget} />;
}
